<template>
  <article class="headcount">
    <div class="headcount__display">
      <header>
        <div class="top__title">
          <!-- <img src="@/assets/images/icon-headcount.svg" alt="총 인원수" /> -->
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512">
            <path
              d="M96 128a128 128 0 1 1 256 0A128 128 0 1 1 96 128zM0 482.3C0 383.8 79.8 304 178.3 304h91.4C368.2 304 448 383.8 448 482.3c0 16.4-13.3 29.7-29.7 29.7H29.7C13.3 512 0 498.7 0 482.3zM609.3 512H471.4c5.4-9.4 8.6-20.3 8.6-32v-8c0-60.7-27.1-115.2-69.8-151.8c2.4-.1 4.7-.2 7.1-.2h61.4C567.8 320 640 392.2 640 481.3c0 17-13.8 30.7-30.7 30.7zM432 256c-31 0-59-12.6-79.3-32.9C372.4 196.5 384 163.6 384 128c0-26.8-6.6-52.1-18.3-74.3C384.3 40.1 407.2 32 432 32c61.9 0 112 50.1 112 112s-50.1 112-112 112z"
            />
          </svg>
          <span>{{ t('title.total_headcount') }}</span>
        </div>
        <div class="title__datetime">{{ clock }}</div>
      </header>
      <div class="headcount__contents">
        <div class="contents__description">
          <i18n-t keypath="general.headcount_description" tag="span">
            <template #total_headcount>
              <b>{{ t('general.total_headcount') }}</b>
            </template>
          </i18n-t>
        </div>
        <div class="contents__headcount">
          <span>{{ headcount || 0 }}</span> <b>{{ t('label.people') }}</b>
        </div>
      </div>
    </div>
    <div class="headcount__control">
      <div class="headcount__keypad">
        <BaseKeypad v-model="headcount" :max-length="2" />
      </div>
      <div class="headcount__button">
        <BaseButtonGroup :direction="'column'">
          <BaseButton v-if="!configStore.useOneMenuPerPerson" class="outline" @button-click="handlePrevClick">
            <template #icon>
              <svg xmlns="http://www.w3.org/2000/svg" width="29.442" height="25.247" viewBox="0 0 29.442 25.247">
                <path
                  id="arrow-left-solid"
                  d="M.641,75.063a2.106,2.106,0,0,0,0,2.977L11.157,88.556a2.105,2.105,0,0,0,2.977-2.977L7.2,78.652H27.364a2.1,2.1,0,1,0,0-4.206H7.207l6.921-6.927a2.105,2.105,0,1,0-2.977-2.977L.635,75.057Z"
                  transform="translate(-0.025 -63.925)"
                />
              </svg>
            </template>
            {{ t('button.prev') }}
          </BaseButton>
          <BaseButton @button-click="handleConfirmClick">{{ t('button.confirm') }}</BaseButton>
        </BaseButtonGroup>
      </div>
    </div>
  </article>
</template>

<script lang="ts" setup>
  import BaseButton from '@/components/base/BaseButton.vue';
  import BaseButtonGroup from '@/components/base/BaseButtonGroup.vue';
  import BaseKeypad from '@/components/base/BaseKeypad.vue';
  import UserError from '@/error';
  import { useConfigStore } from '@/stores/config';
  import { useModalStore } from '@/stores/modal';
  import dayjs from 'dayjs';
  import { onMounted, onUnmounted, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  const { t } = useI18n();
  const configStore = useConfigStore();
  const modalStore = useModalStore();

  let clockInterval: number | undefined;

  const clock = ref('');
  const headcount = ref('');

  onMounted(() => {
    setClock();
    clockInterval = window.setInterval(setClock, 1000);
  });

  onUnmounted(() => {
    if (clockInterval) clearInterval(clockInterval);
  });

  const handlePrevClick = () => {
    modalStore.resolveSyncModal<number>(0);
  };

  const handleConfirmClick = async () => {
    // 인원수 검사
    if (!headcount.value) throw new UserError(t('error.required_headcount'));

    // 인원수 모달 닫고 입력한 인원 수 반환
    modalStore.resolveSyncModal<number>(Number(headcount.value));
  };

  const setClock = () => {
    clock.value = dayjs().format('YYYY-MM-DD HH:mm:ss');
  };
</script>

<style lang="scss" scoped>
  @use '@/assets/scss/variables' as vars;

  .headcount {
    display: grid;
    grid-template-columns: 1fr minmax(200px, 40%);
    width: 100%;
    height: 100%;
    @media (max-width: vars.$md) {
      grid-template-columns: 1fr 1fr;
    }
    @media (max-width: vars.$sm) {
      grid-template-columns: 1fr;
      grid-template-rows: 1fr 1fr;
    }
    .headcount__display {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      background-color: var(--brand-primary);
      padding: 4.4776vw 2.2388vw;
      header {
        display: flex;
        justify-content: space-between;
        padding-bottom: 1.194vw;
        border-bottom: 1px solid #fff;
        .top__title {
          display: flex;
          align-items: center;
          svg {
            fill: var(--brand-primary);
            width: 2.9851vw;
            padding: 4px;
            border-radius: 50%;
            background-color: #fff;
            @media (max-width: vars.$md) {
              width: 22px;
            }
          }
          span {
            position: relative;
            color: #fff;
            font-size: 2.0896vw;
            font-weight: bold;
            margin-left: 0.7463vw;
            z-index: 1;
            @media (max-width: vars.$md) {
              font-size: 20px;
            }
          }
        }
        .title__datetime {
          display: flex;
          align-items: center;
          color: #fff;
          font-size: 1.4925vw;
          @media (max-width: vars.$md) {
            font-size: 14px;
          }
        }
      }
      .headcount__contents {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        height: 100%;
        background: url('@/assets/images/bg-headcount-circle.png') no-repeat center center;
        background-size: 37.3134vw;
        font-size: 1.8657vw;
        @media (max-width: vars.$md) {
          background-size: contain;
          font-size: 18px;
        }
        @media (max-width: vars.$sm) {
          font-size: 14px;
        }
        .contents__description {
          margin-top: 2.2388vw;
        }
        .contents__headcount {
          span {
            font-size: 14.9254vw;
            line-height: 14.9254vw;
            color: var(--brand-primary);
            @media (max-width: vars.$md) {
              font-size: 100px;
              line-height: 100px;
            }

            @media (max-width: vars.$sm) {
              font-size: 80px;
            }
          }
        }
      }
    }
    .headcount__control {
      display: grid;
      grid-template-rows: 1fr auto;
      gap: 2.9851vw;
      align-items: center;
      width: 100%;
      padding: 2.9851vw;
      .headcount__keypad {
        @media (max-width: vars.$sm) {
          height: 100%;
        }
      }
    }
  }
</style>
